import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { withStyles } from '@mui/styles';
import MuiTableCell from '@mui/material/TableCell';
import { useSnackbar } from 'notistack';
import {
  Box,
  IconButton,
  Table,
  TableRow,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
  Dialog
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import EditIcon from '@mui/icons-material/Edit';
import { get } from 'lodash';
import ShortcutIcon from '@mui/icons-material/Shortcut';

import EditMembershipModal from './Modal/EditMembership';
import HeadTable from '../../../../../../common/utils/DataTableHelper/TableHead';
import {
  TableLoader,
  TableNoDataPlaceholder
} from '../../../../../../components/TableLoader';
import { callRest, prettyDate } from '@/common/utils/utils';
import ConvertMembershipModal from './Modal/ConvertMembershipModal';
import AuthUserStore from '@/common/utils/AuthUser';
import { SendWaiverLinkModal } from '@/views/Pages/Appointments/EventView/SendLinkModals/SendWaiverLinkModal';

const TableCell = withStyles({
  root: {
    borderBottom: '1px solid #F4F6F8'
  }
})(MuiTableCell);

const useStyles: any = makeStyles(theme => ({
  Chip: {
    '&.MuiChip-icon': {
      marginLeft: 0,
      width: '20px',
      height: '20px',
      padding: '3px',
      marginRight: '10px',
      borderRadius: '100%',
      border: '1px solid #ffffff'
    }
  },
  Alert: { color: theme.palette.error[600], fontWeight: 400, fontSize: '14px' },
  ActionIcon: {
    color: 'blue',
    fontWeight: 600,
    fontSize: '16px'
  },
  Default: { fontWeight: 400, fontSize: '14px' },
  memDescription: {
    display: 'flex',
    flexDirection: 'row',
    justifyItems: 'center',
    alignItems: 'center'
  },
  AlertIcon: {
    color: theme.palette.error[600],
    fontWeight: 600,
    fontSize: '20px'
  },
  ActionCell: {
    width: 88,
    whiteSpace: 'nowrap'
  },
  SendIcon: {
    fontSize: 20
  },
  space: {
    display: 'inline-block'
  }
}));

const TABLE_HEAD = [
  {
    id: 'Membership',
    label: 'Membership'
  },
  {
    id: 'BillingAmount',
    label: 'Billing Amount'
  },
  {
    id: 'LastBilled',
    label: 'Last Billed Date'
  },
  {
    id: 'nextBillingDate',
    label: 'Next Billing Date'
  },
  {
    id: 'rollsInto',
    label: 'Rolls Into'
  },
  {
    id: 'rollsIntoAmount',
    label: 'Rolls Into Amount'
  },
  {
    id: 'Status',
    label: 'Status'
  },
  {
    id: 'Studio',
    label: 'Studio'
  },
  {
    id: 'Action',
    label: 'Action'
  }
];

const getStoreId = extId => get(atob(extId)?.split(':'), '[1]');

// True when a scheduled cancellation would take effect on or before the next
// scheduled bill (i.e. the membership will never see that next bill go through).
// Only meaningful when item.cancel_date is set — callers check that separately.
const cancelsOnOrBeforeNextBill = item =>
  !item?.next_bill || new Date(item.cancel_date) <= new Date(item.next_bill);

// True when a scheduled cancellation has already taken effect on or before
// today. Both sides are compared as UTC calendar dates (yyyy-MM-dd) — matching
// how these timestamp fields are rendered everywhere else in this file via
// moment.utc(...) — so this doesn't misclassify near midnight for callers in
// a non-UTC timezone.
const isCanceledAsOfToday = item =>
  !!item?.cancel_date &&
  item.cancel_date.slice(0, 10) <= moment.utc().format('YYYY-MM-DD');

const getStatus = item => {
  let MB = '';

  // ----- Membership is from Mindbody --------
  if (!item.is_pow) {
    MB = '[MBO] ';
    if (!item.converted_to) {
      if (!item.is_live) {
        return MB + `Inactive`;
      } else if (item.is_live) {
        return MB + `Active -- Convert Now`;
      }
    } else if (item.converted_to) {
      if (!item.is_live) {
        return MB + `Inactive -- Converted`;
      } else if (item.is_live) {
        return MB + `Active -- Converted`;
      }
    }
  }

  // ----- Membership is Active ---------------
  if (item.is_live) {
    if (item.is_frozen) {
      if (item.freeze_expires) {
        if (new Date() < new Date(item.freeze_expires)) {
          return (
            MB +
            `Frozen until ${moment
              .utc(item.freeze_expires)
              .format('MM/DD/YYYY')}`
          );
        }
      }
      return MB + `Frozen Indefinitely`;
    } else {
      if (item.cancel_date) {
        return MB + `Scheduled to cancel on ${prettyDate(item.cancel_date)}`;
      }
      if (item.freeze_start) {
        if (new Date() < new Date(item.freeze_start)) {
          return MB + `Active. Freezes on ${prettyDate(item.freeze_start)}`;
        }
      }
    }
    return MB + `Active`;
  }
  // ----- Membership is Inactive -------------
  else {
    if (isCanceledAsOfToday(item)) {
      return MB + `Inactive. Canceled on ${prettyDate(item.cancel_date)}`;
    } else if (!item?.last_billed && item?.next_bill) {
      if (item.cancel_date) {
        const nextBillReadable = moment
          .utc(item.next_bill)
          .format('MM/DD/YYYY');
        const cancelDateReadable = prettyDate(item.cancel_date);
        if (cancelsOnOrBeforeNextBill(item)) {
          return (
            MB + `Not Active. Scheduled to cancel on ${cancelDateReadable}`
          );
        }
        return (
          MB +
          `Not Active. Activates on ${nextBillReadable}. Scheduled to cancel on ${cancelDateReadable}`
        );
      }
      return (
        MB +
        `Not Active. Activates on ${moment.utc(item?.next_bill).format('MM/DD/YYYY')}`
      );
    }
    return MB + `Inactive`;
  }
};

// True when a membership won't bill again: either it's not live and its
// cancellation has already taken effect as of today (regardless of exactly
// how cancel_date and next_bill order down to the second), or it has a
// scheduled cancellation that falls on/before its next_bill date (or there's
// no next_bill at all). Applies to both live and not-yet-live memberships.
const willNotBillAgain = item =>
  (!item?.is_live && isCanceledAsOfToday(item)) ||
  (!!item?.cancel_date && cancelsOnOrBeforeNextBill(item));

// One sort rank per distinct getStatus() state (14 = sorts first, 1 = sorts last).
// Mirrors getStatus()'s branching over the same raw fields so sort order doesn't
// depend on string-matching the display copy. Ranks confirmed with the engineer:
// Active > Active. Freezes on > Scheduled to cancel (live) > MBO Active variants >
// Frozen until > Frozen Indefinitely > Not Active. Activates on... (with or without
// a later cancellation) > Not Active. Scheduled to cancel (cancels before activating) >
// Inactive. Canceled > Inactive > MBO Inactive variants.
const SORT_RANK = {
  ACTIVE: 14,
  ACTIVE_FREEZES_ON: 13,
  SCHEDULED_TO_CANCEL_LIVE: 12,
  MBO_ACTIVE_CONVERT_NOW: 11,
  MBO_ACTIVE_CONVERTED: 10,
  FROZEN_UNTIL: 9,
  FROZEN_INDEFINITELY: 8,
  NOT_ACTIVE_ACTIVATES_ON: 7,
  NOT_ACTIVE_ACTIVATES_THEN_CANCELS: 6,
  NOT_ACTIVE_SCHEDULED_TO_CANCEL: 5,
  INACTIVE_CANCELED: 4,
  INACTIVE: 3,
  MBO_INACTIVE_CONVERTED: 2,
  MBO_INACTIVE: 1
};

const getSortPriority = item => {
  // ----- Membership is from Mindbody --------
  if (!item.is_pow) {
    if (!item.converted_to) {
      if (!item.is_live) {
        return SORT_RANK.MBO_INACTIVE;
      } else if (item.is_live) {
        return SORT_RANK.MBO_ACTIVE_CONVERT_NOW;
      }
    } else if (item.converted_to) {
      if (!item.is_live) {
        return SORT_RANK.MBO_INACTIVE_CONVERTED;
      } else if (item.is_live) {
        return SORT_RANK.MBO_ACTIVE_CONVERTED;
      }
    }
  }

  // ----- Membership is Active ---------------
  if (item.is_live) {
    if (item.is_frozen) {
      if (item.freeze_expires) {
        if (new Date() < new Date(item.freeze_expires)) {
          return SORT_RANK.FROZEN_UNTIL;
        }
      }
      return SORT_RANK.FROZEN_INDEFINITELY;
    } else {
      if (item.cancel_date) {
        return SORT_RANK.SCHEDULED_TO_CANCEL_LIVE;
      }
      if (item.freeze_start) {
        if (new Date() < new Date(item.freeze_start)) {
          return SORT_RANK.ACTIVE_FREEZES_ON;
        }
      }
    }
    return SORT_RANK.ACTIVE;
  }
  // ----- Membership is Inactive -------------
  else {
    if (isCanceledAsOfToday(item)) {
      return SORT_RANK.INACTIVE_CANCELED;
    } else if (!item?.last_billed && item?.next_bill) {
      if (item.cancel_date) {
        if (cancelsOnOrBeforeNextBill(item)) {
          return SORT_RANK.NOT_ACTIVE_SCHEDULED_TO_CANCEL;
        }
        return SORT_RANK.NOT_ACTIVE_ACTIVATES_THEN_CANCELS;
      }
      return SORT_RANK.NOT_ACTIVE_ACTIVATES_ON;
    }
    return SORT_RANK.INACTIVE;
  }
};

export const orderByCreatedAt = (a, b) => {
  const aCreatedAt = a?.createdAtReadable;
  const bCreatedAt = b?.createdAtReadable;
  if (aCreatedAt > bCreatedAt) {
    return -1;
  } else if (aCreatedAt < bCreatedAt) {
    return 1;
  } else {
    return 0;
  }
};

export const orderByActive = (a, b) => {
  const aStatus = a.sortPriority || 0;
  const bStatus = b.sortPriority || 0;

  if (aStatus > bStatus) {
    return -1;
  } else if (aStatus < bStatus) {
    return 1;
  } else {
    return 0;
  }
};

export const parseMembership = (sessionStoreId: number, customerAllData, m) => {
  const item = m?.fields;
  const id = m?.pk;
  return {
    id: id,
    lastBilledDate: item?.last_billed,
    needsConversion:
      (item?.is_pow == null || item?.is_pow === 0) &&
      item?.converted_to == null &&
      !item?.is_converting &&
      item?.is_live,
    customerName: `${customerAllData?.firstName} ${customerAllData?.lastName}`,
    description: item?.description || '--',
    isLive: item?.is_live,
    isFrozen: item?.is_frozen,
    legacy: item?.is_pow ? '' : '(MBO)',
    converting: item?.is_converting,
    createdAtReadable: prettyDate(item?.created_at),
    lastBilled: item?.last_billed
      ? moment.utc(item?.last_billed).format('MM/DD/YYYY')
      : '',
    nextBillDateReadable:
      item?.is_frozen ||
      (!item?.is_live && !item?.is_pow) ||
      willNotBillAgain(item)
        ? '--'
        : prettyDate(item?.next_bill)
          ? moment.utc(item?.next_bill).format('MM/DD/YYYY')
          : '',
    // Unlike nextBillDateReadable (the displayed column), this feeds the
    // Edit Membership "Update" form's date input, which edits the raw
    // next_bill field directly — so it always shows the real value rather
    // than collapsing to '--' based on a scheduled cancellation.
    nextBillDate: item?.is_frozen
      ? '--'
      : item?.next_bill
        ? moment.utc(item?.next_bill).format('YYYY-MM-DD')
        : '',
    cancelDate: item?.cancel_date?.slice(0, 10),
    cancelDateReadable: item?.cancel_date ? prettyDate(item.cancel_date) : null,
    freezeStartDate: item?.freeze_start?.slice(0, 10),
    freezeEndDate: item?.freeze_expires?.slice(0, 10),
    freezeStartDateReadable: item?.freeze_start
      ? prettyDate(item.freeze_start)
      : null,
    status: getStatus(item),
    sortPriority: getSortPriority(item),
    isPow: item?.is_pow,
    maxDuration:
      item?.is_frozen && item?.freeze_start && item?.freeze_expires
        ? 90 -
          (Date.parse(item.freeze_expires) - Date.parse(item.freeze_start)) /
            (24 * 3600 * 1000)
        : 90,
    // Can only edit a POW membership. Also, if it is cancelled, you can only un-cancel it has been within 30 days of cancellation
    canEdit:
      item?.is_editable &&
      (item?.is_live ||
        (!item?.last_billed && item?.next_bill && !item?.cancel_date) ||
        (item?.cancel_date &&
          Date.now() - Date.parse(item.cancel_date) < 30 * 24 * 3600 * 1000)), // diff is in miliseconds, so <30 days
    purchasedAtThisStore: sessionStoreId == item?.purchase_store,
    membershipId: id,
    convertedTo: item?.converted_to,
    billingAmount: item?.billing_amount,
    isExpired: !item?.is_live && !item?.is_frozen ? true : false,
    partner: item?.partner
      ? item?.partner?.firstName + ' ' + item?.partner?.lastName
      : null,
    rollsIntoMembership: item?.rolls_into_description,
    nextBillPrice: item?.next_bill_price,
    purchaseStoreCode: item?.purchase_store_code,
    cancellationReason: item?.cancellation_reason,
    agreementUnsigned: !item?.is_agreement_signed
  };
};

export default function CustomerMemberships(props) {
  const { customerAllData } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedData, setSelectedData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConvertLegacyModalOpen, setConvertLegacyModalOpen] = useState(false);
  const [customerMemberships, setCustomerMemberships] = useState([]);
  const [refreshList, setRefreshList] = useState(false);
  const [customerMembershipLoading, setCustomerMembershipLoading] =
    useState(false);
  const [eventReasons, setEventReasons] = useState([]);
  const [eventReasonsLoading, setEventReasonsLoading] = useState(false);
  const [isPaymentMethodOnFile, setIsPaymentMethodOnFile] = useState(false);
  const currentAuthUser = AuthUserStore.getAuthUser();
  const [isSendAgreementOpen, setIsSendAgreementOpen] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    getCustomerMemberships();
  }, []);

  useEffect(() => {
    if (refreshList) {
      getCustomerMemberships();
    }
  }, [refreshList]);

  const handleConvertLegacyModal = row => {
    const isItLegacyMembership = { ...row, isMindBodyMembership: true };
    setSelectedData(isItLegacyMembership);

    setConvertLegacyModalOpen(!isConvertLegacyModalOpen);
  };

  function onEdit(row) {
    setSelectedData(row);
    setIsEditModalOpen(true);
  }

  function onError(msg) {
    enqueueSnackbar(msg, { variant: 'error' });
    setCustomerMembershipLoading(false);
  }

  function onAllMembershipsFetched(r) {
    const fetchedData = r?.data?.data;
    const memberships = fetchedData?.memberships.map(m =>
      parseMembership(
        getStoreId(currentAuthUser?.store?.id),
        customerAllData,
        m
      )
    );
    setCustomerMemberships(
      memberships.sort(orderByCreatedAt).sort(orderByActive)
    );
    setCustomerMembershipLoading(false);
    setIsPaymentMethodOnFile(!!fetchedData?.customer?.default_payment);
  }

  async function getCustomerMemberships() {
    const postVars = { customer: customerAllData?.id };

    if (!customerAllData?.id) {
      // no sense in asking for customer memberships if we don't have a customer id
      return;
    }

    setCustomerMembershipLoading(true);
    setRefreshList(false);
    await callRest(
      '/pos_customer/get_customer_memberships/',
      postVars,
      onAllMembershipsFetched,
      onError
    );
  }

  const sendAgreement = row => {
    setSelectedData(row);
    setIsSendAgreementOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - customerMemberships?.length)
      : 0;

  useEffect(() => {
    setEventReasonsLoading(true);
    callRest(
      '/pos_customer/membership_event_reasons/',
      {},
      res => {
        const data = res.data?.data;
        setEventReasonsLoading(false);
        if (!Object.keys(data).length) {
          enqueueSnackbar('Could not load cancellation reasons.', {
            variant: 'error'
          });
        } else {
          setEventReasons(data);
        }
      },
      () => {
        enqueueSnackbar('Could not load cancellation reasons.', {
          variant: 'error'
        });
        setEventReasonsLoading(false);
      }
    );
  }, []);

  return (
    <div>
      {' '}
      <TableContainer>
        <Table>
          <HeadTable headLabel={TABLE_HEAD} />
          {customerMembershipLoading || eventReasonsLoading ? (
            <TableLoader />
          ) : customerMemberships?.length > 0 ? (
            <TableBody>
              {customerMemberships
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(row => {
                  return (
                    <TableRow key={row.id}>
                      <TableCell
                        style={{ alignItems: 'center', alignContent: 'center' }}
                      >
                        {
                          <Box className={classes.memDescription}>
                            {row?.description}
                          </Box>
                        }
                      </TableCell>
                      <TableCell>
                        {row?.billingAmount
                          ? '$' + parseFloat(row?.billingAmount).toFixed(2)
                          : '--'}
                      </TableCell>
                      <TableCell>{row?.lastBilled || '--'}</TableCell>
                      <TableCell>{row?.nextBillDateReadable || '--'}</TableCell>
                      <TableCell>{row?.rollsIntoMembership || '--'}</TableCell>
                      <TableCell>
                        {row?.rollsIntoMembership && row?.nextBillPrice
                          ? '$' + parseFloat(row?.nextBillPrice).toFixed(2)
                          : '--'}
                      </TableCell>
                      <TableCell>
                        <Typography
                          className={
                            row?.needsConversion
                              ? classes.Alert
                              : classes.Default
                          }
                        >
                          {row?.status}
                        </Typography>
                      </TableCell>
                      <TableCell>{row?.purchaseStoreCode}</TableCell>
                      <TableCell
                        className={classes.ActionCell}
                        title={
                          !row?.purchasedAtThisStore
                            ? 'Membership was purchased from another studio.'
                            : null
                        }
                      >
                        {row?.canEdit && !row?.needsConversion ? (
                          <IconButton
                            disabled={!row?.purchasedAtThisStore}
                            onClick={() => onEdit(row)}
                          >
                            <EditIcon
                              fontSize="small"
                              className={classes.Chip}
                            />
                          </IconButton>
                        ) : (
                          ''
                        )}
                        {row?.needsConversion && (
                          <IconButton
                            disabled={!row?.purchasedAtThisStore}
                            onClick={() => handleConvertLegacyModal(row)}
                          >
                            <EditIcon className={classes.AlertIcon} />
                          </IconButton>
                        )}
                        {row?.agreementUnsigned && (
                          <div className={classes.space}>
                            <IconButton title="Send Membership Agreement">
                              <ShortcutIcon
                                className={classes.SendIcon}
                                onClick={() => sendAgreement(row)}
                              />
                            </IconButton>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          ) : (
            <TableNoDataPlaceholder />
          )}
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          page={page}
          component="div"
          count={customerMemberships?.length}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[10, 50, 100]}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      {isEditModalOpen && (
        <EditMembershipModal
          customerData={customerAllData}
          customerMembership={selectedData}
          onClose={() => setIsEditModalOpen(false)}
          onMembershipChange={getCustomerMemberships}
          setRefreshList={setRefreshList}
          isPaymentMethodOnFile={isPaymentMethodOnFile}
          eventReasons={eventReasons}
        />
      )}
      {isConvertLegacyModalOpen && (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <ConvertMembershipModal
            oldMembership={selectedData}
            setModalOpen={setConvertLegacyModalOpen}
            setRefreshList={setRefreshList}
            isPaymentMethodOnFile={isPaymentMethodOnFile}
            eventReasons={eventReasons['convert']}
          />
        </Dialog>
      )}
      {isSendAgreementOpen && (
        <SendWaiverLinkModal
          title={'Send Membership Agreement to ' + customerAllData.firstName}
          customer={{
            guid: customerAllData.guid,
            mobilePhone: customerAllData.mobilePhone,
            email: customerAllData.email,
            id: customerAllData.id
          }}
          onClose={() => {
            setSelectedData(undefined);
            setIsSendAgreementOpen(false);
          }}
          dataSource={[
            {
              what: 'membership_agreement',
              membership_id: selectedData.id,
              store_id: AuthUserStore.getStoreId()
            }
          ]}
        />
      )}
    </div>
  );
}
